import React from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from '../../../util/reactIntl';
import css from './NoSearchResultsMaybe.module.css';

/**
 * NoSearchResultsMaybe Component
 *
 * Renders an engaging, user-friendly empty state when search filters yield no results.
 * Instead of a dead-end message, it explains the situation warmly and provides
 * a high-converting Call-to-Action to explore all available Arabic tutors and courses.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.listingsAreLoaded - Whether search listings request has completed
 * @param {number} props.totalItems - Total search result count
 * @param {Object} props.location - Current react-router location object
 * @param {Function} props.resetAll - Callback to reset all search filter parameters
 */
const NoSearchResultsMaybe = props => {
  const { listingsAreLoaded, totalItems, location, resetAll } = props;
  const history = useHistory();

  const hasNoResult = listingsAreLoaded && totalItems === 0;
  if (!hasNoResult) {
    return null;
  }

  const hasSearchParams = location?.search?.length > 0;

  /**
   * Resets active search filters to present the entire tutor catalog.
   */
  const handleExploreAll = e => {
    if (e && e.preventDefault) e.preventDefault();
    if (resetAll) {
      resetAll(e);
    } else {
      history.push('/s');
    }
  };

  return (
    <div className={css.noSearchResultsContainer} role="region" aria-label="Search results empty state">
      {/* Decorative Warm Icon Badge */}
      <div className={css.iconCircle} aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          <line x1="9" y1="7" x2="15" y2="7" />
          <line x1="9" y1="11" x2="13" y2="11" />
        </svg>
      </div>

      {/* Main Heading */}
      <h3 className={css.title}>
        <FormattedMessage
          id="SearchPage.noResultsTitle"
          defaultMessage="No tutors available for this course yet"
        />
      </h3>

      {/* Explanatory Friendly Description */}
      <p className={css.description}>
        <FormattedMessage
          id="SearchPage.noResultsDescription"
          defaultMessage="No tutor has onboarded for this specific course yet, but we have a wide variety of Arabic courses and expert native tutors that may match your learning goals. Click below to explore all available tutors."
        />
      </p>

      {/* Action CTA Buttons */}
      <div className={css.actionsGroup}>
        <button
          type="button"
          className={css.exploreCtaBtn}
          onClick={handleExploreAll}
        >
          <span>
            <FormattedMessage
              id="SearchPage.exploreAllTutors"
              defaultMessage="Explore All Tutors & Courses"
            />
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>

        {hasSearchParams && (
          <button
            type="button"
            className={css.clearFiltersLink}
            onClick={e => resetAll && resetAll(e)}
          >
            <FormattedMessage
              id="SearchPage.resetAllFilters"
              defaultMessage="Clear current filters"
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default NoSearchResultsMaybe;
